import { Banner } from "components/DesignSystem/Banner/Banner";
import { ClockCountdown } from "components/icons/ClockCountdown";
import { Info } from "components/icons/Info";
import { LoadingIcon } from "components/icons/LoadingIcon";
import { SLA, TIMED } from "constants/chatSettingBannerTypes";
import { useAppSelector } from "hooks/useAppSelector";
import { useChannelEscalationStatus } from "hooks/useChannelEscalationStatus";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { useIsGroupV2Platform } from "hooks/useIsGroupV2Platform";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { HUMAN_SUPPORT_STATUS } from "store/apis/chatHumanSupport";
import { useGetCurrentBannerQuery } from "store/apis/chatSettings";
import { useGetTaskFromChannelIdQuery } from "store/apis/task";
import { useChannelStateContext } from "stream-chat-react";
import { CHURNED } from "types/contants/crm";

const InfoLoading = () => (
  <div className="t-h-11 t-mx-4 t-bg-neutral-10 t-mb-2 t-flex t-justify-center t-items-center t-rounded">
    <span className="t-inline-flex t-origin-center t-animate-spin">
      <LoadingIcon />
    </span>
  </div>
);

const AdminMessageInputInfo = () => {
  const { channel } = useChannelStateContext();
  const channelId = channel.id || "";

  const {
    data: channelTask,
    isLoading,
    isSuccess,
  } = useGetTaskFromChannelIdQuery(
    {
      channelId: channelId,
    },
    {
      skip: !channelId,
    }
  );

  const { company_group } = channelTask || {};

  if (!isSuccess || isLoading) return <InfoLoading />;

  if (company_group?.ops_stage === CHURNED) {
    return (
      <div className="t-mx-4 t-mb-2">
        <Banner
          type="warning"
          description="This customer has already churned."
        />
      </div>
    );
  }

  return null;
};

export const MessageInputInfo = ({
  isThreadMessage,
  hideSLABanner = false,
}: {
  isThreadMessage?: boolean;
  hideSLABanner?: boolean;
}) => {
  const { escalationStatus } = useChannelEscalationStatus();
  const isV2Platform = useIsGroupV2Platform();
  const { isCpa } = useRoleBasedView();
  const isSmallScreen = useIsSmallScreen();
  const group = useCurrentGroupContext();
  const { showAIChannels, showOnlyAdminMessage } = useAppSelector(
    (state) => state.chatSearch
  );
  const aiMessageViewOnly = showAIChannels && !showOnlyAdminMessage;
  const {
    data: currentChatBanner,
    isSuccess,
    isLoading,
  } = useGetCurrentBannerQuery(
    {
      groupId: group.uuid,
    },
    {
      skip: !group.uuid,
    }
  );

  if (aiMessageViewOnly) {
    return null;
  }

  if (isThreadMessage) {
    return (
      <div className="t-flex t-items-center t-gap-1">
        <span className="t-text-neutral-40 t-mb-1">
          <Info stroke="1.4" size="12" />
        </span>
        <div className="t-text-caption t-text-neutral-40">
          Our AI agent is still in Beta version, it can make mistakes
        </div>
      </div>
    );
  }

  if (isCpa) return <AdminMessageInputInfo />;

  if (hideSLABanner) {
    return null;
  }

  // Skeleton only reserves space for banners this breakpoint actually renders
  if (!isSuccess || isLoading) return isSmallScreen ? null : <InfoLoading />;

  const isHumanSupportEscalated =
    escalationStatus === HUMAN_SUPPORT_STATUS.ACTIVE ||
    escalationStatus === HUMAN_SUPPORT_STATUS.PENDING;

  if (isV2Platform && !isHumanSupportEscalated) {
    return null;
  }

  if (
    currentChatBanner?.banner_type === SLA ||
    currentChatBanner?.banner_type === TIMED
  ) {
    // Eats too much space above the composer on mobile
    if (isSmallScreen) return null;

    return (
      <div className="t-text-neutral-40 t-mx-4 t-flex t-gap-1.5 t-items-center t-text-body-sm t-py-2 t-bg-surface-grey">
        <span className="t-flex-none">
          <ClockCountdown />
        </span>
        <div>{currentChatBanner.banner_text}</div>
      </div>
    );
  }

  return (
    <div className="t-text-neutral-40 t-px-4 t-py-1 t-w-full t-bg-surface-grey">
      <div className="t-bg-blue-0 t-px-3 t-py-1.5 t-border-solid t-border t-border-blue-10 t-rounded-lg t-flex t-gap-2 t-items-center t-w-full">
        <span className="t-text-blue t-flex">
          <Info stroke="1.5" size="20" />
        </span>
        <p className="t-m-0 t-text-body-sm t-text-blue-90">
          {currentChatBanner?.banner_text}
        </p>
      </div>
    </div>
  );
};
